import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faComments,
  faChartLine,
  faPaperPlane,
  faSearch,
  faHeart,
  faCheckCircle,
  faBrain,
  faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";
import IceBreakerGuide from "./IceBreakerGuide";

/* -------------------------------------------------
   DUMMY DATABASE
-------------------------------------------------- */

const conflictsDB = [
  {
    id: 1,
    name: "Rohit Sharma",
    relationship: "Friend",
    conflictType: "Individual ↔ Individual",
    reason: "Ego clash / Miscommunication",
    intensity: "Medium",
    duration: "2 weeks"
  },
  {
    id: 2,
    name: "Startup Co-Founder",
    relationship: "Work",
    conflictType: "Individual ↔ Individual",
    reason: "Power & Decision Control",
    intensity: "High",
    duration: "3 months"
  },
  {
    id: 3,
    name: "Self",
    relationship: "Internal",
    conflictType: "Internal",
    reason: "Self-doubt / Fear",
    intensity: "Low",
    duration: "Ongoing"
  }
];

/* -------------------------------------------------
   THINKY AI DUMMY LOGIC
-------------------------------------------------- */

const thinkyEngine = (step, technique) => {
  const prompts = {
    emotional: [
      "I’ve been feeling some tension and want to acknowledge it calmly.",
      "I think we both might have been hurt — I genuinely want to listen.",
      "Thank you for giving me this chance to reconnect."
    ],
    reframing: [
      "Looking back, I realize we may have seen the situation differently.",
      "Maybe we were both trying to protect something important.",
      "I want to understand your perspective better."
    ],
    curiosity: [
      "What mattered most to you in that moment?",
      "What do you wish I had understood earlier?",
      "How can we avoid this gap in the future?"
    ],
    apology: [
      "I want to say sorry if I hurt you, without making excuses.",
      "I acknowledge my part in this misunderstanding.",
      "Can we start fresh and rebuild trust?"
    ],
    cooling: [
      "Let’s take a moment, then reconnect when ready.",
      "Time helps us see things clearly — can we try later?",
      "Re-engaging after space often works best."
    ]
  };

  return prompts[technique][step] || "I appreciate us talking openly again.";
};

/* -------------------------------------------------
   MAIN COMPONENT
-------------------------------------------------- */

export default function IceBreakerHome() {
  const [consentGiven, setConsentGiven] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [technique, setTechnique] = useState("emotional");
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState("");
  const [sentiment, setSentiment] = useState(null);
  const [resolved, setResolved] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  /* -------------------------------------------------
     HANDLERS
  -------------------------------------------------- */

  const startIceBreaker = (conflict) => {
    setSelectedConflict(conflict);
    setDraft(thinkyEngine(0, technique));
    setStep(0);
    setSentiment(null);
    setResolved(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sendMessage = () => {
    const simulatedSentiment =
      selectedConflict.intensity === "High" && step === 0
        ? "negative"
        : step >= 2
        ? "positive"
        : "neutral";

    setSentiment(simulatedSentiment);

    if (simulatedSentiment === "positive") {
      setResolved(true);
    } else {
      const nextStep = step + 1;
      setStep(nextStep);
      setDraft(thinkyEngine(nextStep, technique));
    }
  };

  const filteredConflicts = conflictsDB.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  /* -------------------------------------------------
     UI
  -------------------------------------------------- */

  return (
    <div className="min-h-screen bg-black text-violet-200 p-4 space-y-6">
      {/* HERO */}
      <header className="text-center space-y-2">
        <h1 className="text-lg lg:text-xl font-bold text-yellow-200">❄️ IceBreaker</h1>
        <p className="text-sm text-white">
          Reconnect • Resolve • Restore relationships — guided by Thinky AI
        </p>
      </header>        
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        {/* Info Box */}
        <div className="flex-1 bg-zinc-900 p-3 rounded-xl text-left text-sm space-y-1 min-w-0">
          <p className="text-sm lg:text-lg truncate">✅ Not therapy, not judgmental</p>
          <p className="text-sm lg:text-lg truncate">✅ Consent-first approach</p>
          <p className="text-sm lg:text-lg truncate">✅ AI guides, you control the conversation</p>
        </div>

        {/* Toggle Button */}
        <div className="flex-shrink-0">
          <button
            onClick={() => setShowGuide(!showGuide)}
            className="bg-violet-600 hover:bg-violet-500 w-64 text-sm lg:text-lg px-3 py-2 rounded-md text-white flex items-center justify-center gap-2"
          >
            {showGuide ? "Hide Guide" : "Show Guide"}
            <FontAwesomeIcon icon={faBrain} />
          </button>
        </div>
      </div>
      <div className="w-full mt-4">
        {showGuide && <IceBreakerGuide onClose={() => setShowGuide(false)} />}
      </div>

      {/* CONSENT */}
      {!consentGiven && (
        <section className="bg-zinc-900 p-4 rounded-xl text-center space-y-3">
          <FontAwesomeIcon icon={faHeart} className="text-violet-400 text-2xl" />
          <p>
            IceBreaker helps initiate sensitive conversations respectfully.
            Proceed only if all parties consent.
          </p>
          <button
            onClick={() => setConsentGiven(true)}
            className="bg-violet-600 text-sm lg:text-lg px-4 py-2 rounded-lg text-white w-64"
          >
            I Understand & Consent
          </button>
        </section>
      )}

      {consentGiven && (
        <>
          {/* SEARCH */}
          <section>
            <div className="flex items-center gap-2 bg-zinc-900 rounded-lg px-3 py-2 mb-4">
              <FontAwesomeIcon icon={faSearch} />
              <input
                className="bg-transparent outline-none w-full text-sm"
                placeholder="Search conflicts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </section>

          {/* CONFLICTS LIST */}
          <section className="space-y-3">
            {filteredConflicts.map((c) => (
              <div
                key={c.id}
                className="bg-zinc-900 p-3 rounded-xl space-y-1"
              >
                <div className="font-semibold text-sm lg:text-lg">{c.name}</div>
                <div className="text-sm text-violet-400">
                  {c.relationship} • {c.conflictType} • {c.reason} • {c.intensity} • {c.duration}
                </div>
                <button
                  onClick={() => startIceBreaker(c)}
                  className="mt-2 bg-violet-700 px-3 py-1 rounded-md text-sm w-32"
                >
                  Start IceBreaking
                </button>
              </div>
            ))}
          </section>

          {/* ICEBREAKING TECHNIQUE SELECTOR */}
          {selectedConflict && (
            <section className="bg-zinc-900 p-4 rounded-xl space-y-4">
              <h2 className="text-sm font-semibold mb-2">Select Icebreaking Approach</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: "emotional", label: "Emotional ❤️", desc: "Acknowledge feelings without judgment" },
                  { key: "reframing", label: "Reframing 🔄", desc: "See the situation from a new perspective" },
                  { key: "curiosity", label: "Curiosity ❓", desc: "Ask questions to understand, not to win" },
                  { key: "apology", label: "Apology 🙏", desc: "Help apologize without losing face" },
                  { key: "cooling", label: "Cooling ⏳", desc: "Take a pause before reconnecting" }
                ].map((t) => (
                  <div
                    key={t.key}
                    className={`bg-zinc-800 p-3 rounded-xl flex flex-col justify-between shadow-md hover:shadow-xl transition cursor-pointer ${
                      technique === t.key ? "border-2 border-violet-500" : "border border-zinc-700"
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="font-semibold text-sm lg:text-lg text-violet-200">{t.label}</div>
                      <p className="text-sm text-violet-400">{t.desc}</p>
                    </div>
                    <button
                      onClick={() => { 
                        setTechnique(t.key); 
                        setDraft(thinkyEngine(0, t.key)); 
                        setStep(0); 
                        setResolved(false); 
                        setSentiment(null); 
                      }}
                      className={`mt-2 px-3 py-1 rounded-md text-sm w-24 text-center ${
                        technique === t.key ? "bg-violet-600 text-white" : "bg-violet-700 text-white/80 hover:bg-violet-600"
                      }`}
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>

              <p className="text-sm text-violet-400 italic mt-2">
                Explanation: This technique guides how Thinky will draft your messages.
              </p>
            </section>
          )}

          {/* THINKY AI INFO */}
          {selectedConflict && (
            <section className="bg-zinc-900 p-4 rounded-xl text-sm space-y-1">
              <FontAwesomeIcon icon={faBrain} /> Thinky is acting as a neutral mediator.
              <ul className="list-disc list-inside text-violet-400">
                <li>Non-judgmental</li>
                <li>Guides safe conversation openings</li>
                <li>Respects privacy & consent</li>
                <li>Tracks progress & suggests reflection</li>
              </ul>
            </section>
          )}

          {/* GUIDED CONVERSATION */}
          {selectedConflict && (
            <section className="bg-zinc-900 p-4 rounded-xl space-y-3">
              <h2 className="text-sm lg:text-lg font-semibold">Conversation with {selectedConflict.name}</h2>
              {!resolved ? (
                <>
                  <textarea
                    className="w-full h-20 bg-black p-3 rounded-lg text-sm outline-none"
                    rows={4}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <button
                    onClick={sendMessage}
                    className="bg-violet-600 px-4 py-2 rounded-lg flex items-center gap-2 w-24"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} /> Send
                  </button>
                  {sentiment && (
                    <p className="text-sm text-violet-400">
                      Simulated response sentiment: <strong>{sentiment}</strong>
                    </p>
                  )}
                </>
              ) : (
                <div className="text-center space-y-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-3xl" />
                  <p className="text-sm">Conversation successfully reopened 🎉</p>
                </div>
              )}
            </section>
          )}

          {/* OUTCOME & REFLECTION */}
          {selectedConflict && (
            <section className="bg-zinc-900 p-4 rounded-xl space-y-2 text-sm">
              <p className="text-sm lg:text-lg">✔ Conflict addressed: <strong>{selectedConflict.name}</strong></p>
              <p className="text-sm lg:text-lg">✔ Positive outcome: <strong>{resolved ? "Yes" : "Pending"}</strong></p>
              <p className="text-sm lg:text-lg">✔ Steps taken: <strong>{step + 1}</strong></p>
              <p className="italic text-violet-400 text-sm lg:text-lg">
                Reflection: Thinky can guide you to explore root causes (Think5Whys ready).
              </p>
            </section>
          )}

          {/* NEXT STEPS & ESCALATION */}
          {selectedConflict && (
            <section className="bg-zinc-900 p-4 rounded-xl text-sm space-y-2">
              <FontAwesomeIcon icon={faTriangleExclamation} /> Suggested Next Steps:
              <ul className="list-disc list-inside text-violet-400">
                <li>Continue conversation gradually</li>
                <li>Take breaks if tension rises</li>
                <li>Involve a trusted human mediator if needed</li>
              </ul>
              <p className="text-sm text-gray-500">
                Note: Always ensure consent and respect privacy.
              </p>
            </section>
          )}
        </>
      )}
    </div>
  );
}
